
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Instagram, Twitter, Linkedin, Youtube, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface SocialMediaLink {
  id: string;
  platform: string;
  url: string;
  is_active: boolean;
}

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState<SocialMediaLink[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const { data } = await supabase
        .from('social_media_links')
        .select('*')
        .eq('is_active', true);
      setSocialLinks(data || []);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const getSocialIcon = (platform: string) => {
    const baseIconProps = "w-3 h-3 xs:w-4 xs:h-4 sm:w-4 sm:h-4 mr-1 xs:mr-2 group-hover:scale-110 transition-transform";
    
    switch (platform) {
      case 'twitter':
        return <Twitter className={`${baseIconProps} text-[hsl(var(--twitter-blue))]`} />;
      case 'instagram':
        return <Instagram className={`${baseIconProps} text-[hsl(var(--instagram-gradient-start))]`} />;
      case 'linkedin':
        return <Linkedin className={`${baseIconProps} text-[hsl(var(--linkedin-blue))]`} />;
      case 'youtube':
        return <Youtube className={`${baseIconProps} text-[hsl(var(--youtube-red))]`} />;
      default:
        return null;
    }
  };

  const handleApiReferenceClick = () => {
    toast.info('API Reference is in development. Please contact our sales team for more updates and early access.');
  };

  const handleSectionClick = (sectionId: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  const blogPosts = [
    {
      title: "Getting Started with AI Image Generation",
      description: "Learn the fundamentals of AI-powered image creation and harness this revolutionary technology",
      slug: "getting-started-ai-image-generation"
    },
    {
      title: "Optimizing Your Creative Workflow with AI", 
      description: "Discover how to streamline your creative process using AI tools and techniques",
      slug: "creative-workflow-optimization"
    },
    {
      title: "Creating Compelling Marketing Visuals with AI",
      description: "Learn how to use AI image generation to create stunning marketing materials", 
      slug: "marketing-visuals-ai"
    },
    {
      title: "Automating Visual Content Creation for Business", 
      description: "Explore how businesses can implement AI-driven automation to scale visual content",
      slug: "business-automation-visual-content"
    }
  ];

  return (
    <footer className="bg-gray-900 text-white py-4 xs:py-6 sm:py-8 lg:py-[30px]">
      <div className="container mx-auto px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 xl:grid-cols-7 gap-4 xs:gap-6 sm:gap-8 lg:gap-10">
          {/* Brand Section */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2">
            <div className="flex items-center space-x-2 xs:space-x-3 mb-3 xs:mb-4 sm:mb-6">
              <div className="w-5 h-5 xs:w-6 xs:h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xs sm:text-sm">BG</span>
              </div>
              <span className="text-sm xs:text-base sm:text-lg xl:text-xl font-bold leading-tight">The Bulk Image Generator</span>
            </div>
            <p className="text-gray-400 mb-3 xs:mb-4 sm:mb-6 max-w-md text-xs xs:text-sm sm:text-base leading-relaxed">
              Transform your creative workflow with AI-powered bulk image generation. 
              Create stunning visuals at scale with the power of advanced AI models.
            </p>
            <div className="flex flex-wrap gap-1 xs:gap-2 sm:gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.url}
                  className="text-gray-400 hover:text-white transition-colors flex items-center group text-xs xs:text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getSocialIcon(social.platform)}
                  <span className="capitalize">{social.platform}</span>
                </a>
              ))}
            </div>
          </div>
          
          {/* Product Section */}
          <div className="col-span-1">
            <h3 className="font-semibold mb-2 xs:mb-3 sm:mb-4 lg:mb-6 text-sm xs:text-base sm:text-lg md:text-base lg:text-base text-white">
              Product
            </h3>
            <ul className="space-y-1.5 xs:space-y-2 sm:space-y-2.5 lg:space-y-3 text-gray-400">
              <li>
                <button 
                  onClick={() => handleSectionClick('features')}
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform text-left w-full"
                >
                  Features
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleSectionClick('pricing')}
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform text-left w-full"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleSectionClick('how-it-works')}
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform text-left w-full"
                >
                  How It Works
                </button>
              </li>
              <li>
                <Link 
                  to="/dashboard" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  About
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Support Section */}
          <div className="col-span-1">
            <h3 className="font-semibold mb-2 xs:mb-3 sm:mb-4 lg:mb-6 text-sm xs:text-base sm:text-lg md:text-base lg:text-base text-white">
              Support
            </h3>
            <ul className="space-y-1.5 xs:space-y-2 sm:space-y-2.5 lg:space-y-3 text-gray-400">
              <li>
                <Link 
                  to="/tools/image-enhancer" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  Free Image Enhancer
                </Link>
              </li>
              <li>
                <Link 
                  to="/docs" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Documentation
                </Link>
              </li>
              <li>
                <Link 
                  to="/prompt-library" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Prompt Library
                </Link>
              </li>
              <li>
                <div className="flex items-center space-x-1 xs:space-x-2 py-1 xs:py-1.5">
                  <button 
                    onClick={handleApiReferenceClick}
                    className="hover:text-white transition-colors duration-200 text-left text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                  >
                    API Reference
                  </button>
                  <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-300 border-blue-500/30 px-1.5 py-0.5">
                    Coming Soon
                  </Badge>
                </div>
              </li>
              <li>
                <Link 
                  to="/help" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Help Center
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/help/videos" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Video Tutorials
                </Link>
              </li>
            </ul>
          </div>

          {/* US Popular Tools */}
          <div className="col-span-1">
            <h3 className="font-semibold mb-2 xs:mb-3 sm:mb-4 lg:mb-6 text-sm xs:text-base sm:text-lg md:text-base lg:text-base text-white">
              Popular in US
            </h3>
            <ul className="space-y-1.5 xs:space-y-2 sm:space-y-2.5 lg:space-y-3 text-gray-400">
              <li>
                <Link 
                  to="/ai-product-photography-amazon" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for Amazon FBA
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-shopify" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for Shopify
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-for-etsy" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for Etsy Sellers
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-ad-creative-generator" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI Ad Creative
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-ebay" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for eBay
                </Link>
              </li>
            </ul>
          </div>

          {/* UK Popular Tools */}
          <div className="col-span-1">
            <h3 className="font-semibold mb-2 xs:mb-3 sm:mb-4 lg:mb-6 text-sm xs:text-base sm:text-lg md:text-base lg:text-base text-white">
              Popular in UK
            </h3>
            <ul className="space-y-1.5 xs:space-y-2 sm:space-y-2.5 lg:space-y-3 text-gray-400">
              <li>
                <Link 
                  to="/ai-image-generator-amazon-uk" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for Amazon UK
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-ebay-uk" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for eBay UK
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-depop" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for Depop
                </Link>
              </li>
              <li>
                <Link 
                  to="/ai-image-generator-notonthehighstreet" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  AI for NOTHS
                </Link>
              </li>
              <li>
                <Link 
                  to="/free-ai-image-generator" 
                  className="hover:text-white transition-colors duration-200 block py-1 xs:py-1.5 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm leading-relaxed hover:translate-x-1 transform transition-transform"
                >
                  Free AI Generator
                </Link>
              </li>
            </ul>
          </div>

          {/* Blog & Guides Section */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <h3 className="font-semibold mb-2 xs:mb-3 sm:mb-4 text-sm xs:text-base sm:text-lg md:text-base lg:text-base text-white">Blog & Guides</h3>
            <ul className="space-y-1 xs:space-y-2 text-gray-400 text-xs xs:text-sm">
              {blogPosts.slice(0, 4).map((post, index) => (
                <li key={index}>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="hover:text-white transition-colors leading-relaxed block py-1 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm hover:translate-x-1 transform transition-transform" 
                    title={post.description}
                  >
                    {post.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/blog" 
                  className="hover:text-white transition-colors font-medium inline-flex items-center group py-1 text-xs xs:text-sm sm:text-base md:text-sm lg:text-sm"
                >
                  View All Guides
                  <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-6 xs:mt-8 sm:mt-12 pt-4 xs:pt-6 sm:pt-8 flex flex-col xs:flex-row justify-between items-center gap-3 xs:gap-4">
          <p className="text-gray-400 text-xs xs:text-sm text-center xs:text-left">
            © 2025 The Bulk Image Generator. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center xs:justify-end gap-3 xs:gap-4 sm:gap-6 text-gray-400 text-xs xs:text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
